import type { EditorDoc, FurnitureEntry, SpeakerEntry } from './editorState';
import { seatOnFloor } from './speakerHeight';

type Position = SpeakerEntry['position'];
type Dims = EditorDoc['dims'];

/** How far from a wall, the floor or the ceiling a kept speaker or listener
 *  is pulled back to when the scanned room is smaller than the drawn one. */
const CLEARANCE_M = 0.1;

function clamp(v: number, lo: number, hi: number): number {
  return hi < lo ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v));
}

function pointInside(p: Position, dims: Dims): Position {
  return [
    clamp(p[0], CLEARANCE_M, dims.Lx - CLEARANCE_M),
    clamp(p[1], CLEARANCE_M, dims.H - CLEARANCE_M),
    clamp(p[2], CLEARANCE_M, dims.Ly - CLEARANCE_M),
  ];
}

// Furniture keeps its height (y is h/2 on the floor); only the plan position
// moves, far enough that the box's half-size stays inside the walls.
function furnitureInside(f: FurnitureEntry, dims: Dims): FurnitureEntry {
  const hw = f.size.w / 2;
  const hd = f.size.d / 2;
  return {
    ...f,
    position: [clamp(f.position[0], hw, dims.Lx - hw), f.position[1], clamp(f.position[2], hd, dims.Ly - hd)],
  };
}

function withoutWallKeys<T>(map: Record<string, T> | undefined): Record<string, T> | undefined {
  if (!map) return map;
  return Object.fromEntries(Object.entries(map).filter(([key]) => !key.startsWith('wall:')));
}

/**
 * Puts a scanned room's geometry into the room being edited, keeping what the
 * user already told us (goal answers, speaker setup, materials, guided step).
 *
 * The scan replaces the shell: dims, footprint, shape, wings, ceiling, windows,
 * doors, and furniture when the scan mapped any. What hung on the OLD shell
 * goes: panels, media and no-go zones, plus per-wall colour and material
 * overrides, because a scan renumbers the walls (`wall:<i>`) and those would
 * land on the wrong one. Speakers, the listener and any kept furniture stay,
 * pulled inside the new bounds.
 */
export function applyScannedGeometry(current: EditorDoc, scanned: EditorDoc): EditorDoc {
  const dims = scanned.dims;
  const furniture = scanned.furniture.length > 0 ? scanned.furniture : current.furniture;
  return {
    ...current,
    dims,
    roomShape: scanned.roomShape,
    footprint: scanned.footprint,
    wings: scanned.wings,
    ceiling: scanned.ceiling,
    windows: scanned.windows ?? [],
    doors: scanned.doors ?? [],
    furniture: furniture.map((f) => furnitureInside(f, dims)),
    panels: [],
    media: [],
    noGoZones: [],
    surfaceColors: withoutWallKeys(current.surfaceColors),
    surfaceMaterials: withoutWallKeys(current.surfaceMaterials),
    // A tower or a sub is not pulled up off the floor with the rest: it
    // stands on it (`seatOnFloor`).
    speakers: current.speakers.map((s) => seatOnFloor({ ...s, position: pointInside(s.position, dims) })),
    listener: current.listener
      ? { ...current.listener, position: pointInside(current.listener.position, dims) }
      : null,
    // A LiDAR scan or a video scan: whichever produced `scanned`.
    source: scanned.source === 'video_scan' ? 'video_scan' : 'lidar_scan',
    scanMetadata: scanned.scanMetadata,
  };
}
